/**
 * Scene Tools
 *
 * AI agent tools for creating and manipulating scene objects. The shared scene
 * tools (create, duplicate, delete, pose, parent, rename, select, show/hide,
 * color, query, get_transform, arrange, undo/redo, focus_camera, screenshot)
 * are trace-viewer's (`shared-scene-tools.ts`); this file adds this editor's
 * own: its extra create templates, console logs and save.
 */

import type { ToolDefinition } from './types';
import type { ToolContext } from '../agent-helpers';
import { applyTransform, groundAlign, recordCreateForUndo } from '../agent-helpers';
import { Renderer } from 'trace-viewer/behaviors';
import { createTimelineWidget } from '../../timeline-widget';
import { readTransformArgs, resolveObjectRef, sceneTool, topLevelParent } from 'trace-viewer/editor';
import { playModeRefusalText } from './authoring-ops';
import { sharedSceneTool } from './shared-scene-tools';
import { installConsoleCapture, getEntries, clearEntries, entryCount, type ConsoleEntry } from './console-buffer';
import { getSceneSaver, isSceneDirty } from '../../save-bridge';

// Start capturing console output as early as possible.
installConsoleCapture();

// ── create_object: this editor's own templates ─────────────

/**
 * The templates this editor makes itself: a demo model and its timeline UI.
 * Every other template is the shared create_object's.
 */
const EDITOR_TEMPLATES = ['astronaut', 'timeline-widget'];

const sharedCreate = sharedSceneTool('create_object');
const sharedProperties = sharedCreate.tool.input_schema.properties as Record<string, Record<string, unknown>>;
const sharedTemplates = sharedProperties.template.enum as string[];

async function createEditorTemplate(input: Record<string, unknown>, ctx: ToolContext): Promise<string> {
    const template = input.template as string;
    const transformArgs = readTransformArgs(input);
    if ('error' in transformArgs) return transformArgs.error;
    const { position, rotation, scale } = transformArgs;
    const active = input.active;
    if (active !== undefined && typeof active !== 'boolean') return `Error: active must be true or false, not ${JSON.stringify(active)}.`;
    if (input.position_relative && !position) return `Error: position_relative is not available for the "${template}" template; give it a position.`;
    const playing = playModeRefusalText(ctx);
    if (playing) return `Error creating object: ${playing}`;
    const { traceScene } = ctx;

    // "At the top" is the document root, never the engine mount point.
    let parentObj = topLevelParent(traceScene);
    if (typeof input.parent === 'string' && input.parent) {
        const found = resolveObjectRef(traceScene, input.parent);
        if ('error' in found) return found.error === 'NOT_FOUND' ? `Error: Parent object "${input.parent}" not found.` : `Error: ${found.message}`;
        parentObj = found.object;
    }

    try {
        if (template === 'astronaut') {
            const viewer = ctx.viewer;
            if (!viewer?.import) return 'Error: No trace-viewer element available in this context.';
            const { sceneObject: newObject } = await viewer.import(
                'https://storageacceausprod.blob.core.windows.net/trace-cms-assets/639063468329689182_6951b5fa30754a0d95a4d0b62f65ff1f.glb',
                typeof input.name === 'string' ? input.name : 'Astronaut',
            );
            if (!newObject.getBehavior(Renderer)) await newObject.addBehavior(Renderer);
            groundAlign(newObject.transform);
            applyTransform(newObject.transform, position, rotation, scale);
            if (active === false) newObject.setActive(false);
            recordCreateForUndo(newObject, parentObj ?? null);
            traceScene.select(newObject);
            ctx.onSceneChanged?.();
            return `Created astronaut "${newObject.name}".`;
        }
        const newObject = await createTimelineWidget(traceScene, parentObj);
        applyTransform(newObject.transform, position, rotation, scale);
        if (active === false) newObject.setActive(false);
        recordCreateForUndo(newObject, parentObj ?? null);
        traceScene.select(newObject);
        ctx.onSceneChanged?.();
        return `Created timeline widget "${newObject.name}". It drives all root Timeline behaviors as a global timeline.`;
    } catch (err) {
        return `Error creating object: ${err instanceof Error ? err.message : String(err)}`;
    }
}

const createObjectTool: ToolDefinition = {
    tool: {
        ...sharedCreate.tool,
        input_schema: {
            ...sharedCreate.tool.input_schema,
            properties: {
                ...sharedProperties,
                template: { ...sharedProperties.template, enum: [...sharedTemplates, ...EDITOR_TEMPLATES] },
            },
        },
    },
    execute: (input, ctx) => {
        const template = input.template;
        if (typeof template === 'string' && EDITOR_TEMPLATES.includes(template)) return createEditorTemplate(input, ctx);
        if (typeof template !== 'string' || !sharedTemplates.includes(template)) {
            return Promise.resolve(`Error: Invalid template "${String(template)}". Valid templates: ${[...sharedTemplates, ...EDITOR_TEMPLATES].join(', ')}`);
        }
        return sharedCreate.execute(input, ctx);
    },
};

// ── framing ────────────────────────────────────────────────

/** The framing inputs focus_camera and screenshot share, from the viewer's schema: the bench counts calls that carry any. */
export const FRAMING_INPUT_KEYS = Object.keys(sceneTool('focus_camera')!.inputSchema.properties);

/** True when a call carries any framing input at all. */
export function hasFramingInput(input: Record<string, unknown>): boolean {
    return FRAMING_INPUT_KEYS.some(key => input[key] !== undefined);
}

// ── get_console_logs ──────────────────────────────────────

function executeGetConsoleLogs(
    input: Record<string, unknown>,
): string {
    const tail = input.tail as number | undefined;
    const offset = input.offset as number | undefined;
    const limit = input.limit as number | undefined;
    const type = input.type as ConsoleEntry['type'] | undefined;
    const clear = input.clear as boolean | undefined;

    const result = getEntries({ tail, offset, limit, type });
    const total = entryCount();

    if (clear) clearEntries();

    if (result.length === 0) {
        return `No console logs${type ? ` of type "${type}"` : ''} (${total} total buffered).`;
    }

    const lines = result.map(e => {
        const ts = new Date(e.timestamp).toISOString().slice(11, 23); // HH:MM:SS.mmm
        return `[${ts}] ${e.type.toUpperCase().padEnd(5)} ${e.text}`;
    });

    const header = `Showing ${result.length} of ${total} entries${clear ? ' (buffer cleared)' : ''}:`;
    return header + '\n' + lines.join('\n');
}

async function executeSaveScene(): Promise<string> {
    const saver = getSceneSaver();
    if (!saver) {
        return 'Error: no save route available (running headless or the sandbox ' +
            'has not registered a saver). Scene edits remain runtime-only.';
    }
    const dirty = isSceneDirty();
    if (dirty === false) {
        return 'Scene is already saved — no unsaved edits. Nothing to do.';
    }
    try {
        return await saver();
    } catch (err) {
        return `Error saving scene: ${err instanceof Error ? err.message : String(err)}`;
    }
}

// ── Exports ────────────────────────────────────────────────

export const sceneTools: ToolDefinition[] = [
    createObjectTool,
    sharedSceneTool('duplicate_object'),
    sharedSceneTool('delete_object'),
    sharedSceneTool('set_transform'),
    sharedSceneTool('arrange_objects'),
    sharedSceneTool('get_transform'),
    sharedSceneTool('focus_camera'),
    sharedSceneTool('select_object'),
    sharedSceneTool('query_objects'),
    sharedSceneTool('set_parent'),
    sharedSceneTool('set_color'),
    sharedSceneTool('set_opacity'),
    sharedSceneTool('set_active'),
    sharedSceneTool('rename_object'),
    sharedSceneTool('screenshot'),
    {
        tool: {
            name: 'get_console_logs',
            input_schema: {
                type: 'object' as const,
                properties: {
                    tail: { type: 'number' },
                    offset: { type: 'number' },
                    limit: { type: 'number' },
                    type: {
                        type: 'string',
                        enum: ['log', 'warn', 'error', 'debug', 'info'],
                    },
                    clear: { type: 'boolean' },
                },
            },
        },
        execute: (input) => Promise.resolve(executeGetConsoleLogs(input)),
    },
    sharedSceneTool('undo'),
    sharedSceneTool('redo'),
    {
        tool: {
            name: 'save_scene',
            input_schema: {
                type: 'object' as const,
                properties: {},
            },
        },
        execute: () => executeSaveScene(),
    },
];
